import type {
  ActivityItem,
  NotificationItem,
  Participant,
  ProjectSection,
  ProjectSummary,
  ProjectTask,
  MyWorkItem,
  RiskItem,
  RichTextDocument,
  SessionUser,
  TaskComment,
  TaskFile,
  TaskHealth,
  TaskStatus,
  Visibility,
} from "@/types/models";
import { calculateTaskProgress } from "@/utils/progress";
import { todayLocalDate } from "@/utils/dates";

type Row = Record<string, unknown>;
const richTextDocument = (value: unknown): RichTextDocument | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  if (record.version !== 1 || !record.doc || typeof record.doc !== "object")
    return undefined;
  return value as RichTextDocument;
};
const words = (value: unknown) =>
  String(value || "")
    .split(/\s+/)
    .filter(Boolean);
export const initials = (value: unknown) =>
  words(value)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "—";
const titleCase = (value: unknown) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const projectRoleLabel = (value: unknown, internal: boolean) => {
  const labels: Record<string, string> = {
    owner: "Project owner",
    internal_editor: "Internal member",
    internal_member: "Internal member",
    client_lead: "Client lead",
    client_member: "Client member",
    observer: "Observer",
  };
  return (
    labels[String(value || "")] ||
    (internal ? "Internal member" : "Client member")
  );
};
const isoTime = (value: unknown) => {
  if (typeof value === "string") return value;
  if (
    value &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof value.toDate === "function"
  )
    return value.toDate().toISOString();
  return "";
};

export function toVisibility(value: unknown): Visibility {
  return value === "internal" || value === "Internal" ? "Internal" : "Shared";
}
export function fromVisibility(value: Visibility | undefined) {
  return value === "Internal" ? "internal" : "shared";
}
export function toStatus(value: unknown): TaskStatus {
  const map: Record<string, TaskStatus> = {
    not_started: "Not started",
    in_progress: "In progress",
    blocked: "Blocked",
    in_review: "In review",
    complete: "Complete",
    not_applicable: "Not applicable",
    cancelled: "Cancelled",
  };
  return map[String(value)] || (value as TaskStatus) || "Not started";
}
export function fromStatus(value: TaskStatus | undefined) {
  return value?.toLowerCase().replaceAll(" ", "_");
}

function health(row: Row): TaskHealth {
  if (row.status === "blocked" || row.status === "Blocked") return "Blocked";
  if (
    !["complete", "Complete", "not_applicable", "Not applicable"].includes(
      String(row.status),
    ) &&
    typeof row.dueDate === "string" &&
    row.dueDate < todayLocalDate()
  )
    return "Overdue";
  return "On track";
}

export function sessionDto(value: unknown): SessionUser {
  const envelope = value as { actor: Row; capabilities?: Row };
  const actor = envelope.actor || {};
  const internal = actor.actorClass === "internal";
  return {
    id: String(actor.id || ""),
    displayName: String(actor.displayName || "Project participant"),
    email: String(actor.email || ""),
    kind: internal ? "internal" : "client",
    initials: initials(actor.displayName),
    photoUrl: actor.photoDataUrl ? String(actor.photoDataUrl) : undefined,
    organization: internal ? "VIPdesk" : "Client organization",
    role:
      actor.workspaceRole === "admin"
        ? "Workspace administrator"
        : internal
          ? "VIPdesk participant"
          : "Client participant",
    canCreateProjects: envelope.capabilities?.createProject === true,
  };
}

export function projectDto(row: Row): ProjectSummary {
  const blockers = Number(
    row.scheduleBlockerCount ??
      Number(row.internalBlockerCount || 0) +
        Number(row.sharedBlockerCount || 0),
  );
  const earliestIncompleteDueDate = String(
    row.scheduleEarliestIncompleteDueDate || "",
  );
  const beyondTargetCount = Number(row.scheduleBeyondTargetCount || 0);
  const overdue =
    earliestIncompleteDueDate && earliestIncompleteDueDate < todayLocalDate()
      ? 1
      : 0;
  const name = String(row.name || "Untitled project");
  return {
    id: String(row.id || ""),
    code: String(row.code || ""),
    name,
    client: String(
      row.clientOrganizationName ||
        (name.toLowerCase().includes("thorne")
          ? "Thorne"
          : "Client organization"),
    ),
    clientOrganizationId: row.clientOrganizationId
      ? String(row.clientOrganizationId)
      : undefined,
    clientOrganizationLogoDataUrl: row.clientOrganizationLogoDataUrl
      ? String(row.clientOrganizationLogoDataUrl)
      : undefined,
    description: String(row.description || ""),
    descriptionRichText: richTextDocument(row.descriptionRichText),
    ownerUpdate: String(
      row.ownerUpdate ||
        "No current outlook has been published for this project.",
    ),
    ownerUpdateBy: String(row.ownerUpdateByDisplayName || "Project owner"),
    ownerUpdateAt: isoTime(row.ownerUpdateAt) || "",
    startDate: String(row.startDate || ""),
    targetEndDate: String(row.targetEndDate || ""),
    workingWeekdays: Array.isArray(row.workingWeekdays)
      ? row.workingWeekdays.map(Number)
      : [1, 2, 3, 4, 5],
    holidays: Array.isArray(row.holidays) ? row.holidays.map(String) : [],
    progress: Number(row.progress || 0),
    health:
      overdue || beyondTargetCount
        ? "Off track"
        : blockers
          ? "At risk"
          : "On track",
    status: titleCase(row.status || "Active") as ProjectSummary["status"],
    nextMilestone: String(row.nextMilestone || "Add the next milestone"),
    nextMilestoneDate: String(row.nextMilestoneDate || row.targetEndDate || ""),
    blockers,
    overdue,
    updatedAt: isoTime(row.updatedAt)
      ? new Intl.DateTimeFormat("en-CA", {
          month: "short",
          day: "numeric",
        }).format(new Date(isoTime(row.updatedAt)))
      : "Recently",
    targetPolicy: row.boundaryPolicy === "warn" ? "Warn only" : "Hard boundary",
    defaultVisibility: toVisibility(row.defaultVisibility),
    scheduleVersion: Number(row.scheduleVersion || 1),
    version: Number(row.version || 1),
  };
}

export function participantDto(row: Row): Participant {
  const name = String(row.displayName || "Project participant");
  const internal = row.actorClass === "internal";
  return {
    id: String(row.actorId || row.directoryProfileId || row.id || ""),
    name,
    initials: initials(name),
    photoUrl: row.photoDataUrl ? String(row.photoDataUrl) : undefined,
    email: String(row.email || ""),
    organization: internal ? "VIPdesk" : "Thorne",
    jobTitle: row.jobTitle ? String(row.jobTitle) : undefined,
    role: projectRoleLabel(row.role, internal),
    kind: internal ? "internal" : "client",
    state: ["pending", "invited"].includes(String(row.status))
      ? "pending"
      : "active",
    version: Number(row.version || 1),
  };
}

export function taskDto(row: Row, participants: Participant[]): ProjectTask {
  const owner = participants.find((person) => person.id === row.ownerActorId);
  const type =
    row.type === "milestone"
      ? "milestone"
      : row.type === "summary"
        ? "summary"
        : row.type === "checklist"
          ? "checklist"
          : row.type === "questionnaire"
            ? "questionnaire"
            : "task";
  const status = toStatus(row.status);
  return {
    id: String(row.id || ""),
    projectId: String(row.projectId || ""),
    sectionId: String(row.sectionId || ""),
    parentId: row.parentTaskId ? String(row.parentTaskId) : undefined,
    level: row.parentTaskId ? 1 : 0,
    type,
    title: String(row.title || "Untitled task"),
    description: String(row.description || ""),
    descriptionRichText: richTextDocument(row.descriptionRichText),
    notes: type === "task" ? String(row.notes || "") : "",
    notesRichText:
      type === "task" ? richTextDocument(row.notesRichText) : undefined,
    ownerId: row.ownerActorId ? String(row.ownerActorId) : undefined,
    ownerName: owner?.name || "Unassigned",
    ownerInitials: owner?.initials || "—",
    ownerPhotoUrl: owner?.photoUrl,
    ownerOrganization: owner?.organization || "No organization",
    startDate: String(row.startDate || ""),
    dueDate: String(row.dueDate || row.startDate || ""),
    duration: Number(row.durationDays || (type === "milestone" ? 0 : 1)),
    status,
    health: health(row),
    visibility: toVisibility(row.visibility),
    progress:
      type === "milestone"
        ? ["Complete", "Not applicable"].includes(status)
          ? 100
          : 0
        : Number(row.progress || 0),
    predecessorIds: [],
    commentCount: 0,
    fileCount: 0,
    version: Number(row.version || 1),
    blocker: row.blockerDescription
      ? String(row.blockerDescription)
      : undefined,
    issueOwnerId: row.issueOwnerActorId
      ? String(row.issueOwnerActorId)
      : undefined,
    issueOwnerNeeded: row.issueOwnerNeeded === true,
    checklist: Array.isArray(row.checklist)
      ? row.checklist.map((item) => {
          const entry = item as Row;
          return {
            id: String(entry.id || ""),
            label: String(entry.label || "Checklist item"),
            complete: entry.complete === true,
          };
        })
      : [],
    questions: Array.isArray(row.questions)
      ? row.questions.map((item) => {
          const entry = item as Row;
          const type = [
            "Short text",
            "Long text",
            "Yes or no",
            "Single select",
            "Multi-select",
            "Date",
            "Number",
            "URL",
            "File request",
          ].includes(String(entry.type))
            ? (String(entry.type) as ProjectTask["questions"][number]["type"])
            : "Short text";
          return {
            id: String(entry.id || ""),
            label: String(entry.label || "Question"),
            type,
            required: entry.required === true,
            helperText: entry.helperText ? String(entry.helperText) : undefined,
            options: Array.isArray(entry.options)
              ? entry.options.map(String)
              : undefined,
            answer: entry.answer ? String(entry.answer) : "",
          };
        })
      : [],
    comments: [],
  };
}

export function myWorkDto(value: unknown): MyWorkItem {
  const row = value as { project?: Row; task?: Row };
  const project = row.project || {};
  const task = row.task || {};
  return {
    id: String(task.id || ""),
    projectId: String(project.id || task.projectId || ""),
    projectName: String(project.name || "Project"),
    projectCode: String(project.code || ""),
    title: String(task.title || "Untitled task"),
    status: toStatus(task.status),
    health: health(task),
    dueDate: String(task.dueDate || ""),
    visibility: toVisibility(task.visibility),
  };
}

export function sectionDto(row: Row, tasks: ProjectTask[]): ProjectSection {
  const sectionTasks = tasks.filter((task) => task.sectionId === row.id);
  return {
    id: String(row.id || ""),
    name: String(row.name || "Section"),
    progress: calculateTaskProgress(sectionTasks).progress,
    visibility: toVisibility(row.visibility),
    orderKey: String(row.orderKey || ""),
  };
}

export function fileDto(
  row: Row,
  tasks: ProjectTask[],
  participants: Participant[],
): TaskFile {
  const size = Number(row.sizeBytes || 0);
  const availability = String(row.availabilityState || row.scanState || "");
  const createdAt = isoTime(row.createdAt);
  const uploadExpired =
    availability === "uploading" &&
    createdAt !== "" &&
    Date.now() - new Date(createdAt).getTime() > 15 * 60 * 1000;
  return {
    id: String(row.id || ""),
    name: String(row.displayName || "Project file"),
    context:
      tasks.find((task) => task.id === row.parentId)?.title || "Project files",
    parentType: (["project", "task", "comment", "risk", "response"].includes(
      String(row.parentType),
    )
      ? String(row.parentType)
      : "project") as TaskFile["parentType"],
    parentId: row.parentId ? String(row.parentId) : undefined,
    uploadedBy:
      participants.find((person) => person.id === row.uploaderActorId)?.name ||
      "Project participant",
    updatedAt: createdAt
      ? new Intl.DateTimeFormat("en-CA", {
          month: "short",
          day: "numeric",
        }).format(new Date(createdAt))
      : "Recently",
    size:
      size >= 1048576
        ? `${(size / 1048576).toFixed(1)} MB`
        : `${Math.max(1, Math.round(size / 1024))} KB`,
    type:
      String(row.contentType || "File")
        .split("/")
        .pop()
        ?.toUpperCase() || "File",
    visibility: toVisibility(row.visibility),
    availabilityState: ["available", "clean"].includes(availability)
      ? "Available"
      : availability === "rejected" ||
          availability === "failed" ||
          uploadExpired
        ? "Needs attention"
        : "Uploading",
    version: Number(row.version || 1),
  };
}

export function riskDto(
  row: Row,
  tasks: ProjectTask[],
  participants: Participant[],
): RiskItem {
  return {
    id: String(row.id || ""),
    title: String(row.title || "Untitled risk"),
    kind: row.kind === "issue" ? "Issue" : "Risk",
    impact: titleCase(
      row.impact || row.severity || "Medium",
    ) as RiskItem["impact"],
    owner:
      participants.find((person) => person.id === row.ownerActorId)?.name ||
      "Owner needed",
    dueDate: String(row.dueDate || ""),
    state:
      row.status === "resolved"
        ? "Resolved"
        : row.status === "monitoring"
          ? "Watching"
          : "Open",
    visibility: toVisibility(row.visibility),
    linkedTask:
      tasks.find((task) => task.id === row.taskId)?.title || "Project",
    mitigation: String(row.neededDecision || row.impactNarrative || ""),
  };
}

export function activityDto(
  row: Row,
  participants: Participant[] = [],
): ActivityItem {
  const name = String(row.actorDisplayName || "Project participant");
  const actor = participants.find(
    (person) => person.id === String(row.actorId || ""),
  );
  return {
    id: String(row.id || ""),
    actor: name,
    initials: initials(name),
    photoUrl: actor?.photoUrl,
    action: String(row.summary || "updated"),
    context: titleCase(row.type || "the project"),
    timestamp: isoTime(row.occurredAt)
      ? new Intl.DateTimeFormat("en-CA", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(isoTime(row.occurredAt)))
      : "Recently",
    visibility: toVisibility(row.audience),
  };
}

export function commentDto(
  row: Row,
  participants: Participant[] = [],
): TaskComment {
  const author = String(row.authorDisplayName || "Project participant");
  const actor = participants.find(
    (person) => person.id === String(row.authorActorId || ""),
  );
  const deleted = row.deleted === true;
  return {
    id: String(row.id || ""),
    author: deleted ? "Comment deleted" : author,
    initials: deleted ? "—" : initials(author),
    photoUrl: deleted ? undefined : actor?.photoUrl,
    createdAt: isoTime(row.createdAt)
      ? new Intl.DateTimeFormat("en-CA", {
          dateStyle: "medium",
          timeStyle: "short",
        }).format(new Date(isoTime(row.createdAt)))
      : "Recently",
    body: String(row.body || ""),
    bodyRichText: richTextDocument(row.bodyRichText),
    visibility: toVisibility(row.visibility),
    deleted,
    version: Number(row.version || 1),
  };
}

export function notificationDto(
  row: Row,
  projects: ProjectSummary[],
): NotificationItem {
  const type = String(row.type || "assignment");
  const kind = type.includes("mention")
    ? "mention"
    : type.includes("schedule")
      ? "schedule"
      : type.includes("risk") || type.includes("block")
        ? "risk"
        : "assignment";
  return {
    id: String(row.id || ""),
    title: String(row.title || "Project update"),
    body: String(row.safeExcerpt || "Open the project to review this update."),
    projectName:
      projects.find((project) => project.id === row.projectId)?.name ||
      "Project",
    timestamp: isoTime(row.createdAt)
      ? new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
          Math.round(
            (new Date(isoTime(row.createdAt)).getTime() - Date.now()) /
              86400000,
          ),
          "day",
        )
      : "Recently",
    read: row.read === true,
    kind,
    taskId: row.taskId ? String(row.taskId) : undefined,
  };
}

export function taskPatchDto(
  patch: Partial<ProjectTask>,
  version: number,
): Row {
  const output: Row = { version };
  if (patch.type !== undefined) output.type = patch.type;
  if (patch.title !== undefined) output.title = patch.title;
  if (patch.description !== undefined) output.description = patch.description;
  if (patch.descriptionRichText !== undefined)
    output.descriptionRichText = patch.descriptionRichText;
  if (patch.notes !== undefined) output.notes = patch.notes;
  if (patch.notesRichText !== undefined)
    output.notesRichText = patch.notesRichText;
  if (patch.status !== undefined) output.status = fromStatus(patch.status);
  if (patch.ownerId !== undefined) output.ownerActorId = patch.ownerId || null;
  if (patch.startDate !== undefined) output.startDate = patch.startDate;
  if (patch.dueDate !== undefined) output.dueDate = patch.dueDate;
  if (patch.duration !== undefined) output.durationDays = patch.duration;
  if (patch.progress !== undefined) output.progress = patch.progress;
  if (patch.visibility !== undefined)
    output.visibility = fromVisibility(patch.visibility);
  if (patch.sectionId !== undefined) output.sectionId = patch.sectionId;
  if (patch.parentId !== undefined)
    output.parentTaskId = patch.parentId || null;
  if (patch.blocker !== undefined) output.blockerDescription = patch.blocker;
  if (patch.issueOwnerId !== undefined)
    output.issueOwnerActorId = patch.issueOwnerId || null;
  if (patch.issueOwnerNeeded !== undefined)
    output.issueOwnerNeeded = patch.issueOwnerNeeded;
  if (patch.checklist !== undefined) output.checklist = patch.checklist;
  if (patch.questions !== undefined) output.questions = patch.questions;
  if (patch.reopenReason !== undefined)
    output.reopenReason = patch.reopenReason;
  return output;
}
